import { z } from "zod";
import {
  actionAvailabilitySchema,
  idSchema,
  instantSchema,
  moneySchema,
  reasonSchema,
  sourceDateSchema,
  stageSchema,
  accessStateSchema,
  type ActionAvailability,
} from "@amber/contracts";
import {
  exclusionKindSchema,
  itemKindSchema,
  stageStateSchema,
  stageNextStepSchema,
  selectionReasonSchema,
  type ItemKind,
} from "./items.js";
import { dateConflictViewSchema } from "./dates.js";
import { qualificationSchema } from "./qualification.js";
import { namedOriginalViewSchema } from "./news.js";
import { itemStatusSchema, preciseStateSchema } from "./status.js";
import { ITEM_FORMS, itemReferenceViewSchema, xThreadViewSchema } from "./x.js";

export const COLLECTION_ACTIONS = {
  addItem: "collection.add_item",
  exclude: "collection.exclude_item",
  reconsider: "collection.reconsider_item",
  retry: "collection.retry_stage",
  stop: "collection.stop_retrying",
  reprocess: "collection.reprocess",
  delete: "collection.delete_item",
  restore: "collection.restore_item",
} as const;

export const EDITOR_ONLY_REASON = "Only Editors and above can add, exclude or reconsider items";
export const OPERATOR_RECOVERY_REASON = "Only Operators and Admins can retry, stop retrying or reprocess items";
export const ADMIN_DELETE_REASON = "Only Admins can delete, restrict or restore records";

export const REPROCESS_STAGES = ["download", "archive", "transcribe", "speakers"] as const;
export const reprocessStageSchema = z.enum(REPROCESS_STAGES);

export const MEDIA_TYPES = ["video", "audio", "text"] as const;
export const mediaTypeSchema = z.enum(MEDIA_TYPES);
export type MediaType = z.infer<typeof mediaTypeSchema>;

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = { video: "Video", audio: "Audio", text: "Text" };

export function mediaTypeOf(kind: ItemKind): MediaType {
  if (kind === "video" || kind === "short" || kind === "livestream_replay") return "video";
  if (kind === "episode") return "audio";
  return "text";
}

export const SOURCE_FAMILIES = [
  "official_records",
  "own_channels",
  "broadcasters",
  "podcasts",
  "news_reports",
] as const;
export const sourceFamilySchema = z.enum(SOURCE_FAMILIES);
export type SourceFamily = z.infer<typeof sourceFamilySchema>;

export const SOURCE_FAMILY_LABELS: Record<SourceFamily, string> = {
  official_records: "Official records",
  own_channels: "Own and party channels",
  broadcasters: "Broadcasters",
  podcasts: "Podcasts",
  news_reports: "News reports",
};

export const DISCOVERED_RANGES = ["hour", "today", "7d", "older"] as const;
export const DISCOVERED_FILTERS = ["hour", "today", "24h", "7d", "30d", "older"] as const;
export const discoveredRangeSchema = z.enum(DISCOVERED_FILTERS);
export const DISCOVERED_LABELS: Record<(typeof DISCOVERED_FILTERS)[number], string> = {
  hour: "Last hour",
  today: "Today",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  older: "Older",
};
export const PASSED_CHECKS = ["context", "attribution"] as const;
export const PASSED_LABELS: Record<(typeof PASSED_CHECKS)[number], string> = {
  context: "Context complete",
  attribution: "Attribution accepted",
};

export const SORT_COLUMNS = ["title", "platform", "channel", "status", "type", "published", "discovered"] as const;
export const sortSchema = z.string().regex(new RegExp(`^(${SORT_COLUMNS.join("|")})\\.(asc|desc)$`));

const csv = <T extends z.ZodType<string, string>>(item: T) =>
  z
    .string()
    .transform((v) => v.split(",").filter((t) => t.length > 0))
    .pipe(z.array(item));

/** Which X posts the backlog lists; by default only posts by tracked people, and Editor-added posts always. */
export const POST_AUTHORS = ["tracked", "all"] as const;

export const itemsQuerySchema = z.strictObject({
  status: csv(itemStatusSchema).optional(),
  readiness: csv(z.enum(["draft", "eligible"])).optional(),
  person: csv(idSchema("prf")).optional(),
  source: csv(idSchema("src")).optional(),
  platform: csv(z.string().regex(/^[a-z][a-z0-9_]*$/)).optional(),
  stage: csv(stageSchema).optional(),
  family: csv(z.union([sourceFamilySchema, z.literal("none")])).optional(),
  type: csv(mediaTypeSchema).optional(),
  discovered: discoveredRangeSchema.optional(),
  language: csv(z.string().regex(/^([a-z]{2,3}|none)$/)).optional(),
  counted: z.enum(["yes"]).optional(),
  archived: z.enum(["yes"]).optional(),
  passed: z.enum(PASSED_CHECKS).optional(),
  overdue: z.enum(["yes"]).optional(),
  through: z.enum(["no"]).optional(),
  corrected: z.enum(["yes", "no"]).optional(),
  authors: z.enum(POST_AUTHORS).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  sort: sortSchema.optional(),
  group: z.enum(["status", "none"]).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ItemsQuery = z.infer<typeof itemsQuerySchema>;

/** The backlog's filters as its list query reads them, without paging, sort and grouping. */
export const itemsFilterSchema = itemsQuerySchema.omit({ sort: true, group: true, limit: true, offset: true });
export type ItemsFilter = z.input<typeof itemsFilterSchema>;

export const personRefSchema = z.strictObject({ id: idSchema("prf"), name: z.string() });
export const platformRefSchema = z.strictObject({ key: z.string(), label: z.string(), code: z.string() });

export const stageBarSchema = z.strictObject({
  stage: stageSchema,
  name: z.string(),
  state: stageStateSchema,
  percent: z.number().min(0).max(100).nullable(),
});

export const statusViewSchema = z.strictObject({
  status: itemStatusSchema,
  label: z.string(),
  precise: preciseStateSchema,
  precise_label: z.string(),
  stage: stageSchema,
  stage_name: z.string(),
  title: z.string(),
  reason: z.string().nullable(),
  attempts: z.strictObject({ used: z.number().int(), allowed: z.number().int() }).nullable(),
  next: z.string().nullable(),
  next_at: instantSchema.nullable(),
  stopped: z.strictObject({ by: z.string(), at: instantSchema }).nullable(),
});
export type StatusView = z.infer<typeof statusViewSchema>;

export const itemRowSchema = z.strictObject({
  id: idSchema("itm"),
  title: z.string(),
  people: z.array(personRefSchema),
  platform: platformRefSchema,
  channel: z.string().nullable(),
  original_url: z.string(),
  kind: itemKindSchema,
  media_type: mediaTypeSchema,
  duration_seconds: z.number().int().nullable(),
  size_bytes: z.number().int().nullable(),
  language: z.string().nullable(),
  published: sourceDateSchema.nullable(),
  discovered_at: instantSchema,
  status: statusViewSchema,
  stage_detail: z.string(),
  stages: z.array(stageBarSchema).length(6),
  source_family: sourceFamilySchema.nullable(),
  added_by_link: z.boolean(),
  access_state: accessStateSchema,
  revision: z.number().int().positive(),
  readiness: z
    .strictObject({
      result: z.enum(["draft", "eligible"]),
      label: z.string(),
      reason: z.string().nullable(),
      policy: z.string(),
      communications: z.number().int(),
      people: z.array(
        z.strictObject({
          name: z.string(),
          result: z.enum(["draft", "eligible"]),
          reason: z.string().nullable(),
          communication: z.number().int(),
        }),
      ),
    })
    .nullable(),
});
export type ItemRow = z.infer<typeof itemRowSchema>;

export const facetValueSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  count: z.number().int(),
  disabled_reason: z.string().optional(),
});
export type FacetValue = z.infer<typeof facetValueSchema>;

export const itemsResponseSchema = z.strictObject({
  rows: z.array(itemRowSchema),
  total: z.number().int(),
  communications: z.number().int().nullable(),
  all: z.number().int(),
  /** X posts by other accounts that match the other filters; the default hides them. */
  other_accounts: z.number().int(),
  offset: z.number().int(),
  group_counts: z.record(z.string(), z.number().int()),
  facets: z.strictObject({
    status: z.array(facetValueSchema),
    readiness: z.array(facetValueSchema),
    corrected: z.array(facetValueSchema),
    person: z.array(facetValueSchema),
    platform: z.array(facetValueSchema),
    stage: z.array(facetValueSchema),
    family: z.array(facetValueSchema),
    type: z.array(facetValueSchema),
    discovered: z.array(facetValueSchema),
  }),
  activity: z.array(z.strictObject({ stage: stageSchema, label: z.string(), count: z.number().int() })),
  actions: z.strictObject({ add: actionAvailabilitySchema, recover: actionAvailabilitySchema }),
});
export type ItemsResponse = z.infer<typeof itemsResponseSchema>;

export const stageDetailSchema = z.strictObject({
  stage: stageSchema,
  name: z.string(),
  state: stageStateSchema,
  text: z.string(),
  attempts: z.strictObject({ used: z.number().int(), allowed: z.number().int() }),
  entered_at: instantSchema.nullable(),
  finished_at: instantSchema.nullable(),
  cost: moneySchema.nullable(),
  next_step: stageNextStepSchema.nullable(),
});

export const manualActionSchema = z.strictObject({
  id: idSchema("aud"),
  action: z.string(),
  label: z.string(),
  by: z.string(),
  at: instantSchema,
  reason: z.string().nullable(),
});

export const itemDetailSchema = z.strictObject({
  item: itemRowSchema,
  canonical_url: z.string(),
  selection_reason: selectionReasonSchema,
  selection_label: z.string(),
  discoveries: z.array(
    z.strictObject({
      method: z.string(),
      label: z.string(),
      at: instantSchema,
      by: z.string().nullable(),
      note: z.string().nullable(),
    }),
  ),
  added: z
    .strictObject({ by: z.string(), reason: z.string(), date: sourceDateSchema.nullable(), at: instantSchema })
    .nullable(),
  exclusion: z
    .strictObject({
      kind: exclusionKindSchema,
      label: z.string(),
      by: z.string(),
      reason: z.string(),
      at: instantSchema,
    })
    .nullable(),
  exclusion_history: z.array(
    z.strictObject({
      kind: exclusionKindSchema,
      label: z.string(),
      by: z.string(),
      reason: z.string(),
      at: instantSchema,
      cleared_by: z.string(),
      cleared_at: instantSchema,
      cleared_reason: z.string().nullable(),
    }),
  ),
  dates: z.strictObject({
    event: sourceDateSchema.nullable(),
    published: sourceDateSchema.nullable(),
    updated: sourceDateSchema.nullable(),
    discovered_at: instantSchema,
    retrieved_at: instantSchema.nullable(),
    conflicts: z.array(dateConflictViewSchema),
  }),
  milestones: z.strictObject({
    discovered_at: instantSchema,
    archived_at: instantSchema.nullable(),
    processed_at: instantSchema.nullable(),
    first_eligible_at: instantSchema.nullable(),
    source_available_at: instantSchema.nullable(),
  }),
  stages: z.array(stageDetailSchema).length(6),
  total_cost: moneySchema,
  manual_actions: z.array(manualActionSchema),
  manual_actions_total: z.number().int(),
  engine_run: z.strictObject({ workflow_id: z.string(), run_id: z.string(), href: z.string() }).nullable(),
  archive: z
    .strictObject({
      version: z.number().int().positive(),
      versions: z.number().int().positive(),
      captured_at: instantSchema,
      last_checked_at: instantSchema,
      availability: z.string(),
      artifacts: z.array(
        z.strictObject({
          id: idSchema("art"),
          kind: z.string(),
          size_bytes: z.number().int(),
          mime_type: z.string(),
          href: z.string(),
        }),
      ),
    })
    .nullable(),
  stopped: z.strictObject({ by: z.string(), at: instantSchema, reason: z.string() }).nullable(),
  form: z.enum(ITEM_FORMS).nullable(),
  references: z.array(itemReferenceViewSchema),
  x: xThreadViewSchema.nullable(),
  feed: z
    .strictObject({
      enclosure_url: z.string().nullable(),
      enclosure_bytes: z.number().int().nullable(),
      episode_type: z.string().nullable(),
      guests: z.array(z.string()),
      available_until: instantSchema.nullable(),
      changes: z.array(
        z.strictObject({ kind: z.string(), label: z.string(), at: instantSchema, note: z.string().nullable() }),
      ),
    })
    .nullable(),
  news: z
    .strictObject({
      supplier: z.string(),
      source: z.string(),
      authors: z.array(z.string()),
      text_basis: z.enum(["page_capture", "supplier_text"]).nullable(),
      text_basis_label: z.string().nullable(),
      links: z.array(z.string()),
      named_originals: z.array(namedOriginalViewSchema),
    })
    .nullable(),
  actions: z.strictObject({
    exclude: actionAvailabilitySchema,
    reconsider: actionAvailabilitySchema,
    retry: actionAvailabilitySchema,
    stop: actionAvailabilitySchema,
    reprocess: actionAvailabilitySchema,
  }),
});
export type ItemDetail = z.infer<typeof itemDetailSchema>;

export const URL_CHECK_OUTCOMES = [
  "new",
  "already_collected",
  "source",
  "known_account",
  "not_collected",
  "invalid",
] as const;

export const urlCheckSchema = z.strictObject({
  outcome: z.enum(URL_CHECK_OUTCOMES),
  message: z.string(),
  platform: platformRefSchema.nullable(),
  canonical_url: z.string().nullable(),
  item: z.strictObject({ id: idSchema("itm"), title: z.string(), status: z.string() }).nullable(),
  source: z.strictObject({ id: idSchema("src"), title: z.string() }).optional(),
});
export type UrlCheck = z.infer<typeof urlCheckSchema>;

export const urlCheckQuerySchema = z.strictObject({ url: z.string().min(1).max(2000) });

export const addItemRequestSchema = z.strictObject({
  url: z.string().trim().min(1, "Enter the link to the item").max(2000),
  people: z.array(idSchema("prf")).max(20).default([]),
  date: z.iso.date("Enter the date as YYYY-MM-DD").optional(),
  reason: reasonSchema,
});
export type AddItemRequest = z.input<typeof addItemRequestSchema>;

export const excludeRequestSchema = z.strictObject({
  kind: exclusionKindSchema,
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});
export type ExcludeRequest = z.infer<typeof excludeRequestSchema>;

export const reconsiderRequestSchema = z.strictObject({
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});
export type ReconsiderRequest = z.infer<typeof reconsiderRequestSchema>;

export const EXCLUSION_LABELS = {
  not_relevant: "Not relevant",
  duplicate_handled_elsewhere: "Duplicate handled elsewhere",
  withdrawn: "Withdrawn",
} as const;

export const RECOVERY_ACTIONS = ["retry", "reprocess"] as const;
export const recoveryActionSchema = z.enum(RECOVERY_ACTIONS);

/** DI-120: the most items one bulk recovery takes, as selected rows or as all items matching a filter. */
export const RECOVERY_MAX_ITEMS = 500;
const recoveryItemIds = z.array(idSchema("itm")).min(1).max(RECOVERY_MAX_ITEMS);

export const recoveryPreviewRequestSchema = z
  .strictObject({
    action: recoveryActionSchema,
    item_ids: recoveryItemIds.optional(),
    filter: itemsFilterSchema.optional(),
    from_stage: reprocessStageSchema.optional(),
  })
  .refine((r) => (r.item_ids === undefined) !== (r.filter === undefined), {
    message: "Give either the selected items or the filter",
    path: ["item_ids"],
  })
  .refine((r) => r.action === "retry" || r.from_stage !== undefined, {
    message: "Choose the stage to reprocess from",
    path: ["from_stage"],
  });
export type RecoveryPreviewRequest = z.input<typeof recoveryPreviewRequestSchema>;

// DI-120: with a filter, item_ids are the items its preview resolved, and the command takes those that still match.
export const recoveryRequestSchema = z.strictObject({
  action: recoveryActionSchema,
  item_ids: recoveryItemIds,
  filter: itemsFilterSchema.optional(),
  from_stage: reprocessStageSchema.optional(),
  reason: reasonSchema,
});
export type RecoveryRequest = z.input<typeof recoveryRequestSchema>;

export const recoveryPreviewSchema = z.strictObject({
  action: recoveryActionSchema,
  from_stage: stageSchema.nullable(),
  included: z.array(
    z.strictObject({
      id: idSchema("itm"),
      title: z.string(),
      stage: stageSchema,
      stage_name: z.string(),
      stages: z.string(),
      route: z.string(),
      estimated_cost: moneySchema,
    }),
  ),
  not_included: z.array(z.strictObject({ id: idSchema("itm"), title: z.string(), why: z.string() })),
  hidden: z.number().int().nonnegative(),
  /** Items the filter matched when the preview was made; null for a preview of selected items */
  matching: z.number().int().nonnegative().nullable(),
  attempts: z.number().int().nonnegative(),
  estimated_cost: moneySchema,
  routes: z.array(z.string()),
  warnings: z.array(z.string()),
  notes: z.array(z.string()),
});
export type RecoveryPreview = z.infer<typeof recoveryPreviewSchema>;

export const retryRequestSchema = z.strictObject({ reason: reasonSchema, base_revision: z.number().int().positive() });
export const stopRetryingRequestSchema = z.strictObject({
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});

export const accessChangeRequestSchema = z.strictObject({ reason: reasonSchema });

export const accessPreviewSchema = z.strictObject({
  target: z.enum(["deleted", "restricted", "normal"]),
  item: z.strictObject({ id: idSchema("itm"), title: z.string() }),
  artifacts: z.number().int().nonnegative(),
  versions: z.number().int().nonnegative(),
  effects: z.array(z.string()),
});
export type AccessPreview = z.infer<typeof accessPreviewSchema>;

export const DELETED_TABS = ["deleted", "restricted"] as const;
export const deletedQuerySchema = z.strictObject({ tab: z.enum(DELETED_TABS).optional() });

export const deletedRowSchema = z.strictObject({
  id: z.union([idSchema("itm"), idSchema("prf")]),
  name: z.string(),
  scope: z.string(),
  type: z.enum(["item", "artifact", "profile", "source", "user"]),
  by: z.string(),
  at: instantSchema,
  reason: z.string(),
});
export type DeletedRow = z.infer<typeof deletedRowSchema>;

export const archiveSummarySchema = z.strictObject({
  items: z.number().int().nonnegative(),
  size_bytes: z.number().int().nonnegative(),
  deleted_contributing: z.number().int().nonnegative(),
  label: z.string(),
});
export type ArchiveSummary = z.infer<typeof archiveSummarySchema>;

export interface DeletedList {
  readonly tab: (typeof DELETED_TABS)[number];
  readonly counts: { deleted: number; restricted: number };
  readonly rows: DeletedRow[] | null;
  readonly archive: ArchiveSummary;
  readonly restore: ActionAvailability;
  readonly policy_version: string;
}

export const routeViewSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  use: z.string(),
  kind: z.string(),
  category: z.enum(["acquisition", "processing"]),
  platform: z.string(),
  state: z.enum(["active", "cooling", "quarantined", "releasing", "stopped", "not_ready", "not_configured"]),
  state_label: z.string(),
  state_detail: z
    .strictObject({
      since: instantSchema,
      reason: z.string().nullable(),
      by: z.string().nullable(),
      until: instantSchema.nullable(),
    })
    .nullable(),
  credential: z
    .strictObject({
      name: z.string(),
      expires_at: instantSchema.nullable(),
      last_verified_at: instantSchema.nullable(),
      expiring_soon: z.boolean(),
    })
    .nullable(),
  quota: z.string().nullable(),
  last_failure: z.strictObject({ at: instantSchema, outcome: z.string(), detail: z.string() }).nullable(),
  attempts_7d: z.strictObject({
    total: z.number().int(),
    succeeded: z.number().int(),
    challenged: z.number().int(),
    bytes: z.number().int().nonnegative(),
    median_ms: z.number().int().nullable(),
  }),
  revision: z.number().int().positive().nullable(),
  actions: z.strictObject({ quarantine: actionAvailabilitySchema, release: actionAvailabilitySchema }).nullable(),
  qualification: qualificationSchema,
});
export type RouteView = z.infer<typeof routeViewSchema>;

export const ROUTE_ACTIONS = {
  quarantine: "collection.quarantine_route",
  release: "collection.release_route",
} as const;
export const OPERATOR_ROUTES_REASON = "Only Operators and Admins can quarantine or release routes";

export const routeChangeRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
});

export const quarantinePreviewSchema = z.strictObject({
  route: z.string(),
  queued: z.number().int().nonnegative(),
  moves_to: z.string().nullable(),
  stage: z.string(),
  suggestion: z.string().nullable(),
});
export type QuarantinePreview = z.infer<typeof quarantinePreviewSchema>;

export const releasePreviewSchema = z.strictObject({
  route: z.string(),
  quarantined: z.strictObject({ since: instantSchema, by: z.string().nullable(), reason: z.string().nullable() }),
  held: z.number().int().nonnegative(),
  test: z.string(),
});
export type ReleasePreview = z.infer<typeof releasePreviewSchema>;

export const releaseResultSchema = z.strictObject({
  outcome: z.enum(["released", "test_failed"]),
  detail: z.string(),
  resumed: z.number().int().nonnegative(),
});
export type ReleaseResult = z.infer<typeof releaseResultSchema>;

export const routesResponseSchema = z.strictObject({
  routes: z.array(routeViewSchema),
  issues: z.array(
    z.strictObject({
      id: z.string(),
      platform: z.string(),
      cause: z.string(),
      label: z.string(),
      opened_at: instantSchema,
      expected_reset: instantSchema.nullable(),
      next_step: z.string(),
      evidence: z.string(),
    }),
  ),
});
export type RoutesResponse = z.infer<typeof routesResponseSchema>;

export const communicationFactSchema = z.object({
  communication: z.number().int().positive(),
  counted: z.boolean(),
  eligible: z.boolean(),
  context: z.boolean(),
  attribution: z.boolean(),
  eligible_people: z.array(z.string()),
});
export type CommunicationFact = z.infer<typeof communicationFactSchema>;

export const itemReadinessSchema = z.object({
  result: z.enum(["draft", "eligible"]),
  reason: z.string().nullable(),
  policy: z.object({ name: z.string(), version: z.number().int() }),
  communications: z.number().int(),
  eligible_communications: z.number().int(),
  people: z.array(
    z.object({
      profile_id: z.string().nullable(),
      name: z.string().nullable(),
      result: z.enum(["draft", "eligible"]),
      reason: z.string().nullable(),
      communication: z.number().int(),
    }),
  ),
  assessed_at: z.string(),
});
